'use client'

import React, { useState, useEffect } from 'react';
import { InternalLink } from '../internal-link';
import { APP_LOGIN_URL } from '@/lib/redirect-utils';
import { CountdownDial } from '../CountdownDial';
import { X } from '../icons/X';
import { Clock7 } from 'lucide-react';
import { BUTTON_IDS } from '@/lib/constants';

interface TrialEndingBannerProps {
    trialEndDate?: Date;
    onDismiss?: () => void;
    onCtaClick?: () => void;
}

const TrialEndingBanner: React.FC<TrialEndingBannerProps> = ({
    trialEndDate,
    onDismiss,
    onCtaClick
}) => {
    const [timeRemaining, setTimeRemaining] = useState<{
        days: number;
        hours: number;
        minutes: number;
        seconds: number;
    }>({ days: 0, hours: 0, minutes: 0, seconds: 0 });

    const [isVisible, setIsVisible] = useState(false);
    const [isAnimating, setIsAnimating] = useState(false);
    const [endDate, setEndDate] = useState<Date | null>(null);

    useEffect(() => {
        // Get or set trial end date in localStorage
        const storedEndDate = localStorage.getItem('trialEndDate');
        let finalEndDate: Date;

        if (trialEndDate) {
            finalEndDate = trialEndDate;
            localStorage.setItem('trialEndDate', trialEndDate.toISOString());
        } else if (storedEndDate) {
            finalEndDate = new Date(storedEndDate);
        } else {
            finalEndDate = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
            localStorage.setItem('trialEndDate', finalEndDate.toISOString());
        }

        setEndDate(finalEndDate);

        // Check if banner was previously dismissed
        const isDismissed = localStorage.getItem('trialEndingBannerDismissed');
        if (!isDismissed) {
            setIsVisible(true);
            setTimeout(() => setIsAnimating(true), 50);
        }
    }, [trialEndDate]);

    useEffect(() => {
        if (!endDate) return;

        const updateCountdown = () => {
            const now = new Date().getTime();
            const endTime = endDate.getTime();
            const difference = endTime - now;

            if (difference > 0) {
                const days = Math.floor(difference / (1000 * 60 * 60 * 24));
                const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
                const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
                const seconds = Math.floor((difference % (1000 * 60)) / 1000);

                setTimeRemaining({ days, hours, minutes, seconds });
            } else {
                setTimeRemaining({ days: 0, hours: 0, minutes: 0, seconds: 0 });
            }
        };

        updateCountdown();
        const interval = setInterval(updateCountdown, 1000);

        return () => clearInterval(interval);
    }, [endDate]);

    const handleDismiss = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsAnimating(false);
        setTimeout(() => {
            setIsVisible(false);
            localStorage.setItem('trialEndingBannerDismissed', 'true');
            onDismiss?.();
        }, 300);
    };



    if (!isVisible) return null;

    return (
        <div 
            className={`w-full body-bg backdrop-blur-sm !border-b !border-gray-700 border-opacity-50 shadow-lg transition-all duration-300 relative ${isAnimating ? 'opacity-100' : 'opacity-0 max-h-0 overflow-hidden'}`}
            role="banner"
            aria-live="polite"
        >
            <InternalLink
                id={BUTTON_IDS.GET_STARTED}
                href={APP_LOGIN_URL}
                className="block px-4 py-3"
            >
                <div className="max-w-6xl mx-auto flex items-center justify-center gap-5">
                    <span className="flex items-center gap-2">
                        {/* <Clock7 strokeWidth={1.5} /> */}
                        <h3 className="text-white text-base font-semibold">
                            Your trial ends in
                        </h3>
                    </span>
                    <CountdownDial
                        days={timeRemaining.days}
                        hours={timeRemaining.hours}
                        minutes={timeRemaining.minutes}
                        seconds={timeRemaining.seconds}
                    />
                </div>
            </InternalLink>

            <button
                onClick={handleDismiss}
                className="absolute top-1/2 -translate-y-1/2 right-4 text-white hover:bg-white/10 p-1 rounded transition-colors opacity-60 hover:opacity-100"
                aria-label="Dismiss banner"
                type="button"
            >
                <X size={20} />
            </button>
        </div>
    );
};

export default TrialEndingBanner;
